import { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Truck, Loader2 } from 'lucide-react';
import { vehicleService } from '../services/vehicleService';
import { toast } from 'sonner';

interface Vehicle {
  id: number;
  plateNumber: string;
  type: string;
  capacity: number;
  volumeCapacity: number;
  status: 'available' | 'in-use' | 'maintenance' | 'unavailable' | 'active' | 'inactive';
  currentLocation?: string;
  lastMaintenanceDate?: string;
  nextMaintenanceDate?: string;
  fuelType: string;
  year: number;
}

export function Vehicles() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    plateNumber: '',
    type: '',
    capacity: '',
    volumeCapacity: '',
    status: 'available' as const,
    currentLocation: '',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    fuelType: 'Dizel',
    year: '',
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response = await vehicleService.getVehicles();
      
      if (response.success && response.data) {
        const mappedData: Vehicle[] = response.data.map((v: any) => ({
          id: v.id,
          plateNumber: v.plate_number || v.plateNumber || '',
          type: v.type || v.vehicle_type || '',
          capacity: v.capacity || v.kapasite || 0,
          volumeCapacity: v.volume_capacity || v.volumeCapacity || 0,
          status: mapStatus(v.status),
          currentLocation: v.current_location || v.currentLocation,
          lastMaintenanceDate: v.last_maintenance_date || v.lastMaintenanceDate,
          nextMaintenanceDate: v.next_maintenance_date || v.nextMaintenanceDate,
          fuelType: v.fuel_type || v.fuelType || 'Dizel',
          year: v.year || v.model_year || new Date().getFullYear(),
        }));
        setVehicles(mappedData);
      } else {
        toast.error(response.message || 'Araçlar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Araçlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const mapStatus = (status: string): Vehicle['status'] => {
    switch (status) {
      case 'available':
      case 'active':
        return 'available';
      case 'in-use':
      case 'in_transit':
        return 'in-use';
      case 'maintenance':
        return 'maintenance';
      case 'unavailable':
      case 'inactive':
        return 'unavailable';
      default:
        return 'available';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const vehicleData = {
        plate_number: formData.plateNumber,
        type: formData.type,
        capacity: parseFloat(formData.capacity),
        volume_capacity: parseFloat(formData.volumeCapacity),
        status: formData.status,
        current_location: formData.currentLocation || null,
        last_maintenance_date: formData.lastMaintenanceDate || null,
        next_maintenance_date: formData.nextMaintenanceDate || null,
        fuel_type: formData.fuelType,
        year: parseInt(formData.year),
      };

      let response;
      
      if (editingVehicle) {
        response = await vehicleService.updateVehicle(editingVehicle.id, vehicleData);
        if (response.success) {
          toast.success('Araç güncellendi');
          await loadVehicles();
        } else {
          toast.error(response.message || 'Güncelleme başarısız');
        }
      } else {
        response = await vehicleService.createVehicle(vehicleData);
        if (response.success) {
          toast.success('Araç oluşturuldu');
          await loadVehicles();
        } else {
          toast.error(response.message || 'Oluşturma başarısız');
        }
      }

      resetForm();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      plateNumber: vehicle.plateNumber,
      type: vehicle.type,
      capacity: vehicle.capacity.toString(),
      volumeCapacity: vehicle.volumeCapacity.toString(),
      status: vehicle.status,
      currentLocation: vehicle.currentLocation || '',
      lastMaintenanceDate: vehicle.lastMaintenanceDate || '',
      nextMaintenanceDate: vehicle.nextMaintenanceDate || '',
      fuelType: vehicle.fuelType,
      year: vehicle.year.toString(),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Bu aracı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await vehicleService.deleteVehicle(id);
      if (response.success) {
        toast.success('Araç silindi');
        setVehicles(vehicles.filter(v => v.id !== id));
      } else {
        toast.error(response.message || 'Silme başarısız');
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Silme hatası');
    }
  };

  const resetForm = () => {
    setFormData({
      plateNumber: '',
      type: '',
      capacity: '',
      volumeCapacity: '',
      status: 'available',
      currentLocation: '',
      lastMaintenanceDate: '',
      nextMaintenanceDate: '',
      fuelType: 'Dizel',
      year: '',
    });
    setEditingVehicle(null);
    setIsModalOpen(false);
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || vehicle.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'available': 'bg-green-100 text-green-800',
      'in-use': 'bg-blue-100 text-blue-800',
      'maintenance': 'bg-orange-100 text-orange-800',
      'unavailable': 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      'available': 'Müsait',
      'in-use': 'Kullanımda',
      'maintenance': 'Bakımda',
      'unavailable': 'Kullanılamaz',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Çekici Yönetimi</h1>
          <p className="text-gray-500 mt-1">Filo araçlarını görüntüleyin ve yönetin</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Yeni Araç
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Plaka veya araç tipi ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="available">Müsait</option>
            <option value="in-use">Kullanımda</option>
            <option value="maintenance">Bakımda</option>
            <option value="unavailable">Kullanılamaz</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Toplam Araç</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{vehicles.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Müsait</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {vehicles.filter(v => v.status === 'available').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Kullanımda</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {vehicles.filter(v => v.status === 'in-use').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Bakımda</div>
          <div className="text-2xl font-bold text-orange-600 mt-1">
            {vehicles.filter(v => v.status === 'maintenance').length}
          </div>
        </div>
      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVehicles.map((vehicle) => (
          <div key={vehicle.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Truck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{vehicle.plateNumber}</h3>
                    <p className="text-sm text-gray-500">{vehicle.type}</p>
                  </div>
                </div>
                {getStatusBadge(vehicle.status)}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Kapasite:</span>
                  <span className="font-medium text-gray-900">{vehicle.capacity.toLocaleString()} kg</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Hacim:</span>
                  <span className="font-medium text-gray-900">{vehicle.volumeCapacity} m³</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Yıl:</span>
                  <span className="font-medium text-gray-900">{vehicle.year}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Yakıt:</span>
                  <span className="font-medium text-gray-900">{vehicle.fuelType}</span>
                </div>
                {vehicle.currentLocation && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Konum:</span>
                    <span className="font-medium text-gray-900">{vehicle.currentLocation}</span>
                  </div>
                )}
              </div>

              {vehicle.nextMaintenanceDate && (
                <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-800">
                    Sonraki Bakım: {new Date(vehicle.nextMaintenanceDate).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleEdit(vehicle)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Düzenle
                </button>
                <button
                  onClick={() => handleDelete(vehicle.id)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Sil
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredVehicles.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {searchTerm || filterStatus !== 'all' 
            ? 'Arama sonucu bulunamadı' 
            : 'Henüz araç kaydı yok'}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingVehicle ? 'Aracı Düzenle' : 'Yeni Araç Ekle'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plaka *</label>
                  <input
                    type="text"
                    required
                    value={formData.plateNumber}
                    onChange={(e) => setFormData({...formData, plateNumber: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="34 ABC 123"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Araç Tipi *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seçiniz</option>
                    <option value="TIR">TIR</option>
                    <option value="Kamyon">Kamyon</option>
                    <option value="Kamyonet">Kamyonet</option>
                    <option value="Panelvan">Panelvan</option>
                    <option value="Çekici">Çekici</option>
                    <option value="Dorse">Dorse</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kapasite (kg) *</label>
                  <input
                    type="number"
                    required
                    value={formData.capacity}
                    onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hacim (m³) *</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={formData.volumeCapacity}
                    onChange={(e) => setFormData({...formData, volumeCapacity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durum *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="available">Müsait</option>
                    <option value="in-use">Kullanımda</option>
                    <option value="maintenance">Bakımda</option>
                    <option value="unavailable">Kullanılamaz</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yıl *</label>
                  <input
                    type="number"
                    required
                    min="2000"
                    max="2030"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yakıt Tipi *</label>
                  <select
                    required
                    value={formData.fuelType}
                    onChange={(e) => setFormData({...formData, fuelType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Dizel">Dizel</option>
                    <option value="Benzin">Benzin</option>
                    <option value="LPG">LPG</option>
                    <option value="Elektrik">Elektrik</option>
                    <option value="Hibrit">Hibrit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mevcut Konum</label>
                  <input
                    type="text"
                    value={formData.currentLocation}
                    onChange={(e) => setFormData({...formData, currentLocation: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="İstanbul Depo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Son Bakım Tarihi</label>
                  <input
                    type="date"
                    value={formData.lastMaintenanceDate}
                    onChange={(e) => setFormData({...formData, lastMaintenanceDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sonraki Bakım Tarihi</label>
                  <input
                    type="date"
                    value={formData.nextMaintenanceDate}
                    onChange={(e) => setFormData({...formData, nextMaintenanceDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    editingVehicle ? 'Güncelle' : 'Ekle'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
